import React, { useEffect, useState, useCallback } from "react";
import {
  useStaffAuth
} from "../context/StaffAuthContext";
import {
  createSubject,
  getAllSubjects,
  updateSubject,
  deleteSubject
} from "../api/subjects/subjectApi";
import {
  getAllSubjectsPortal
} from "../api/subjects/subjectPortalApi";
import SubjectCard from "../components/subjects/SubjectCard";
import {
  Plus,
  BookOpen,
  Filter,
  RotateCcw
} from "lucide-react";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
  Textarea,
  Checkbox,
  Skeleton,
  EmptyState
} from "../components/ui";
import {
  useToast
} from "../context/ToastContext";

export default function SubjectList({ isPortal = false }) {
  const { hasPermission } = useStaffAuth();
  const canManage = isPortal ? hasPermission("SUBJECT_MANAGEMENT_FULL") : true;

  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const [filterElective, setFilterElective] = useState("ALL"); // ALL | CORE | ELECTIVE
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    color: "#3B82F6",
    isElective: false,
  });

  const showSuccess = (msg) => {
    toast.success(msg);
  };
  const showError = (msg) => {
    toast.error(msg);
  };

  const fetchSubjects = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (isPortal) {
        res = await getAllSubjectsPortal();
      } else {
        res = await getAllSubjects();
      }
      const list = Array.isArray(res) ? res : res.data || res.subjects || [];
      setSubjects(list);
    } catch (err) {
      showError(err.response?.data?.message || "Failed to fetch subjects");
    } finally {
      setLoading(false);
    }
  }, [isPortal]);

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  const handleEdit = (subject) => {
    setEditingId(subject._id);
    setFormData({
      name: subject.name,
      code: subject.code,
      description: subject.description || "",
      color: subject.color || "#3B82F6",
      isElective: subject.isElective || false,
    });
  };

  const handleResetForm = () => {
    setEditingId(null);
    setFormData({
      name: "",
      code: "",
      description: "",
      color: "#3B82F6",
      isElective: false,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateSubject(editingId, formData);
        showSuccess("Subject updated successfully");
      } else {
        await createSubject(formData);
        showSuccess("Subject created successfully");
      }
      handleResetForm();
      fetchSubjects();
    } catch (err) {
      showError(err.response?.data?.message || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSubject(id);
      showSuccess("Subject deleted");
      fetchSubjects();
    } catch (err) {
      showError(err.response?.data?.message || "Failed to delete subject");
    }
  };

  const filteredSubjects = subjects.filter((s) => {
    if (filterElective === "CORE") return !s.isElective;
    if (filterElective === "ELECTIVE") return s.isElective;
    return true;
  });

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-5 antialiased">
      {/* Header & Filter Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <div className="flex items-center gap-2.5">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
              Subject Management
            </h1>
            <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
              Configure academic subjects and course details
            </p>
          </div>
        </div>

        {/* Filter Switcher */}
        <div className="flex items-center gap-1 bg-neutral-100/80 p-1 rounded-lg self-start sm:self-auto border border-neutral-200/50">
          <Filter className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500 ml-1.5 mr-0.5 shrink-0" />
          <button
            type="button"
            onClick={() => setFilterElective("ALL")}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${ filterElective === "ALL" ? "bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 shadow-xs" : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-100" }`}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setFilterElective("CORE")}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${ filterElective === "CORE" ? "bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 shadow-xs" : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-100" }`}
          >
            Core
          </button>
          <button
            type="button"
            onClick={() => setFilterElective("ELECTIVE")}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${ filterElective === "ELECTIVE" ? "bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 shadow-xs" : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-100" }`}
          >
            Elective
          </button>
        </div>
      </div>

      {/* Notifications / Banners */}
      
      

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left/Main Column: Cards List */}
        <div className={canManage ? "lg:col-span-2 space-y-4" : "lg:col-span-3 space-y-4"}>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Skeleton height={140} width="100%" />
              <Skeleton height={140} width="100%" />
              <Skeleton height={140} width="100%" />
              <Skeleton height={140} width="100%" />
            </div>
          ) : filteredSubjects.length === 0 ? (
            <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900 p-8">
              <EmptyState
                icon={BookOpen}
                title="No subjects added yet"
                description={
                  filterElective !== "ALL"
                    ? `No subjects match the "${filterElective.toLowerCase()}" filter.`
                    : "Get started by adding subjects to your curriculum using the form."
                }
              />
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredSubjects.map((subject) => (
                <SubjectCard
                  key={subject._id}
                  subject={subject}
                  canManage={canManage}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right Column: Create / Edit Form Card */}
        {canManage && (
          <div className="lg:col-span-1">
            <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900 sticky top-20">
              <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 px-4 sm:px-6 py-3">
                <CardTitle className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500" />
                  {editingId ? "Edit Subject" : "Create New Subject"}
                </CardTitle>
              </CardHeader>

              <CardContent className="p-4 sm:p-6 space-y-4">
                <form onSubmit={handleSubmit} className="space-y-3.5">
                  <Input
                    label="Subject Name *"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Mathematics"
                    required
                  />

                  <Input
                    label="Subject Code *"
                    type="text"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value.toUpperCase() })
                    }
                    placeholder="e.g. MATH101"
                    className="uppercase"
                    required
                  />

                  <Textarea
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional brief description..."
                    rows={3}
                  />

                  <div>
                    <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1">
                      Subject Color Tag
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="h-9 w-16 cursor-pointer rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-1"
                      />
                      <span className="text-xs font-mono uppercase text-neutral-600 dark:text-neutral-300 bg-neutral-50 dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 px-2.5 py-1 rounded-md">
                        {formData.color}
                      </span>
                    </div>
                  </div>

                  <div className="pt-1">
                    <Checkbox
                      id="isElective"
                      label="This is an elective subject"
                      checked={formData.isElective}
                      onChange={(e) => setFormData({ ...formData, isElective: e.target.checked })}
                    />
                  </div>

                  <div className="flex gap-2 pt-3">
                    <Button
                      type="submit"
                      variant="primary"
                      className="flex-1 text-xs font-semibold py-2"
                    >
                      {editingId ? "Update Subject" : "Create Subject"}
                    </Button>

                    {editingId && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={handleResetForm}
                        leftIcon={<RotateCcw className="w-3.5 h-3.5" />}
                        className="text-xs border border-neutral-200 dark:border-neutral-800 py-2 px-3"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}